import { useState } from 'react'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'

import Callout from 'modules/core/components/Callout'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import type Employee from 'modules/employees/store/models/Employee'
import { useBulkAssignEmployeesToPolicy } from 'modules/payroll_bundle/hooks/useBulkAssignEmployeesToPolicy'
import { usePolicyAssignmentEligibleEmployees } from 'modules/payroll_bundle/hooks/usePolicyAssignmentEligibleEmployees'
import type Policy from 'modules/payroll_bundle/store/models/Policy'

interface Props {
  policyActiveEmployeeIds: string[]
  employees: Employee[]
  policy: Policy
  onClose: () => void
  onCompleted: (args: {
    employeeSelection: EmployeeSelectorV2MultiSelectState
    policyId: string
  }) => void
}

const AssignEmployeesToPolicyModal = ({
  policy,
  policyActiveEmployeeIds,
  employees,
  onClose,
  onCompleted,
}: Props) => {
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: [],
  })
  const { fetching: fetchingBulkAssignEmployeesToPolicy, update } = useBulkAssignEmployeesToPolicy()
  // We show on the modal only the employees with an active contract that are not already assigned to the policy.
  const eligibleEmployees = employees.filter((employee) => {
    const isActiveOnPolicy =
      !policyActiveEmployeeIds.includes(employee.id) && !employee.isTerminated

    const legalEntity = employeeLegalEntity(employee)
    const matchesCurrencyAndCountry =
      legalEntity.attributes.currency === policy.attributes.currency &&
      legalEntity.attributes.country === policy.attributes.country

    if (policy.attributes.currency !== null && policy.attributes.country !== null) {
      return isActiveOnPolicy && matchesCurrencyAndCountry
    } else {
      return isActiveOnPolicy
    }
  })

  const { fetching, policyAssignmentEligibleEmployeeIds } = usePolicyAssignmentEligibleEmployees(
    eligibleEmployees.map((employee) => employee.id)
  )
  // Employees with historical data are not eligible to be assigned to a policy. In order to know more about how we
  // determine if an employee has historical data, you can check the repository PayrollPolicyAssignmentEligibleEmployees.
  const ineligibleEmployees = policyAssignmentEligibleEmployeeIds
    ? eligibleEmployees.filter(
        (employee) => !policyAssignmentEligibleEmployeeIds.includes(employee.id)
      )
    : eligibleEmployees

  async function handleAssignEmployeesSubmit() {
    const policyId = String(policy.id)

    await update({
      payrollPolicyId: policyId,
      employeeSelection: selectedEmployees,
    })

    onCompleted({ employeeSelection: selectedEmployees, policyId })
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      title={i18n.t(
        'app_store_v2.apps.payroll_bundle.settings.payroll_policy.assign_to_policy_modal.title',
        { policyName: policy.attributes.name }
      )}
      description={i18n.t(
        'app_store_v2.apps.payroll_bundle.settings.payroll_policy.assign_to_policy_modal.description_self_onboarding'
      )}
      primaryAction={{
        label: i18n.t(
          'app_store_v2.apps.payroll_bundle.settings.payroll_policy.assign_to_policy_modal.submit_labeL_self_onboarding'
        ),
        onClick: handleAssignEmployeesSubmit,
        disabled: fetchingBulkAssignEmployeesToPolicy,
        loading: fetchingBulkAssignEmployeesToPolicy,
      }}
      secondaryAction={{
        label: i18n.t(
          'app_store_v2.apps.payroll_bundle.settings.payroll_policy.assign_to_policy_modal.cancel_label'
        ),
        onClick: onClose,
      }}
      onClose={onClose}
    >
      {fetching && (
        <Box alignItems="center" aria-label={i18n.t('common.loading')}>
          <Spinner />
        </Box>
      )}
      {!fetching && (
        <Box gap="s24">
          <EmployeeSelectorV2
            multiple
            onChange={setSelectedEmployees}
            forcedFilters={{
              employeeIds: eligibleEmployees.map((employee) => employee.id),
              excludeIds: ineligibleEmployees.map((employee) => employee.id),
            }}
            options={{
              hideLabel: true,
              asList: true,
            }}
          />

          {policy.attributes.currency !== null && policy.attributes.country !== null && (
            <Callout
              title={i18n.t(
                'payroll_bundle.onboarding.payroll_policy_step.employees_selector_title'
              )}
              type="info"
              description={i18n.t(
                'payroll_bundle.onboarding.payroll_policy_step.form_tips.employees'
              )}
            />
          )}
        </Box>
      )}
    </F0Dialog>
  )
}

export default AssignEmployeesToPolicyModal
